import {makeAutoObservable, observable, runInAction} from 'mobx';
import API from '../../../APIs/API';
import {AddressVerificationRequest, AddressVerificationResponse} from '../../../APIs/types';

export type AddressKey = 'client' | 'policyOwner';

export interface VerificationState {
    result: AddressVerificationResponse | null;
    isVerifying: boolean;
    isStale: boolean;
    missingUnitConfirmed: boolean;
    inputSnapshot: AddressVerificationRequest | null;
    error: boolean;
}

const defaultState = (): VerificationState => ({
    result: null,
    isVerifying: false,
    isStale: false,
    missingUnitConfirmed: false,
    inputSnapshot: null,
    error: false,
});

export class AddressVerificationStore {
    verifications: Map<AddressKey, VerificationState> = observable.map();

    constructor() {
        makeAutoObservable(this);
    }

    getState(key: AddressKey): VerificationState {
        return this.verifications.get(key) ?? defaultState();
    }

    async verifyAddress(key: AddressKey, data: AddressVerificationRequest) {
        this.verifications.set(key, {
            ...defaultState(),
            isVerifying: true,
            inputSnapshot: data,
        });

        try {
            const result = await API.VerifyAddress(data);
            runInAction(() => {
                const state = this.getState(key);
                if (result) {
                    this.verifications.set(key, {
                        ...state,
                        result,
                        isVerifying: false,
                        error: false,
                    });
                } else {
                    this.verifications.set(key, {
                        ...state,
                        isVerifying: false,
                        error: true,
                    });
                }
            });
        } catch {
            runInAction(() => {
                const state = this.getState(key);
                this.verifications.set(key, {
                    ...state,
                    isVerifying: false,
                    error: true,
                });
            });
        }
    }

    markStale(key: AddressKey) {
        const state = this.verifications.get(key);
        if (state) {
            this.verifications.set(key, {...state, isStale: true});
        }
    }

    acceptStandardized(key: AddressKey) {
        const state = this.verifications.get(key);
        if (state?.result?.standardizedAddress) {
            this.verifications.set(key, {
                ...state,
                isStale: false,
                inputSnapshot: state.result.standardizedAddress,
            });
        }
    }

    confirmNoUnit(key: AddressKey, confirmed: boolean) {
        const state = this.verifications.get(key);
        if (state) {
            this.verifications.set(key, {...state, missingUnitConfirmed: confirmed});
        }
    }

    reset(key: AddressKey) {
        this.verifications.delete(key);
    }

    resetAll() {
        this.verifications.clear();
    }

    hydrate(key: AddressKey, snapshot: AddressVerificationRequest) {
        this.verifications.set(key, {
            result: {
                valid: true,
                standardizedAddress: snapshot,
                components: [],
                missingComponents: [],
                verdict: {
                    addressComplete: true,
                    validationGranularity: 'PREMISE',
                    hasInferredComponents: false,
                    hasReplacedComponents: false,
                },
            },
            inputSnapshot: snapshot,
            isStale: false,
            isVerifying: false,
            error: false,
            missingUnitConfirmed: false,
        });
    }

    canSubmit(key: AddressKey): boolean {
        const state = this.getState(key);
        if (!state.result) return false;
        if (state.error) return false;
        if (state.isVerifying) return false;
        if (state.isStale) return false;
        if (!state.result.valid) return false;

        return true;
    }
}
